import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import moment from 'moment'
import classNames from 'classnames'

import { Checkbox } from '@/components/forms/Checkbox'
import { TextInput } from '@/components/forms/TextInput'
import { clearOrgFilter, setDateRangeFilter, setFilter } from '@/reducers/analytics/analytics.redux'
import {
  applyFilters,
  fetchAllData,
  fetchCategoriesByOrgSectionAndPlaybooks,
  fetchPlaybookData,
  fetchVoipCampaignsByOrg,
  fetchPlaybooksByOrg,
  fetchPlaybooksByOrgs,
} from '@/reducers/analytics/analytics.actions'
import { MultiSelect } from '@/components/forms/MultiSelect'
import { getOrganizationOptions, getPlaceholderContent } from '@/utils/helpers'
import { minCallDurationOptions } from '@/utils/constants'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { OrganizationFilter } from '@/components/filters/OrganizationFilter'
import { MultiFilter } from '@/components/filters/MultiFilter'
import { WinsFilter } from '@/components/filters/WinsFilter'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import { OrganizationsFilter } from '@/components/filters/OrganizationsFilter'
import { NestedSelect } from '@/components/forms/NestedSelect'
import { formatNestedOptions } from '@/components/helpers/nestedSelectHelpers'
import Select from '@/components/forms/Select'
import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'

import { ANALYTICS_SECTIONS, ROUTE_TO_SECTION } from './analytics.constants'

const { OVERVIEW, CHECKLIST, DYNAMIC_PROMPT, POSTCALL, USAGE, CSV } = ANALYTICS_SECTIONS

export const AnalyticsFilters = () => {
  const dispatch = useDispatch()
  const [scrolled, setScrolled] = useState(false)
  const [agentSearchQuery, setAgentSearchQuery] = useState('')
  const [dateInvalid, setDateInvalid] = useState(false)
  const { section } = useParams()
  const currentSection = ROUTE_TO_SECTION[section] || section || OVERVIEW
  const { organizations } = useSelector((state) => state)
  const {
    organizationid: organizationId,
    hierarchy_manager: isHierarchyManager,
    own_organization_id,
  } = useSelector((state) => state.currentUser)
  const analyticsStore = useSelector((state) => state.analytics)
  const { data, filters, loading } = analyticsStore
  const isBaltoAdmin = own_organization_id === 1
  const callDurationInvalid =
    Boolean(filters.callDuration) &&
    Boolean(filters.maxCallDuration) &&
    Number(filters.callDuration) >= Number(filters.maxCallDuration)

  const organizationOptions = getOrganizationOptions(organizations)
  const { userOrgHierarchy, flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)
  const sectionsToShowHierarchy = [USAGE, CHECKLIST, DYNAMIC_PROMPT, POSTCALL]
  const isHierarchyVisible =
    currentSection === OVERVIEW || sectionsToShowHierarchy.includes(currentSection)
  const canSeeOrgHierarchyFilter =
    !isBaltoAdmin &&
    isHierarchyVisible &&
    isHierarchyManager &&
    flattenedUserOrgHierarchy.length > 1

  // When a hierarchy manager has selected a single org, use that org for data fetches
  const effectiveOrganization =
    filters.organizations?.length === 1 ? filters.organizations[0] : organizationId

  const sectionNotCsvOrUsage = currentSection !== CSV && currentSection !== USAGE

  const handleFilterChange = (value) => {
    dispatch(setFilter(value))
  }

  const handleDateRangeFilterChange = (value) => {
    const duration = moment.duration(moment(value.endDate).diff(moment(value.startDate)))
    const days = duration.asDays()

    if (days > 62) {
      setDateInvalid(true)
    } else {
      setDateInvalid(false)
    }

    dispatch(setDateRangeFilter(value))
    // Saved Analytics filters keep the exact dates picked, so a click never stores a preset name.
    dispatch(setFilter({ dateRange: '' }))
  }

  const handlePlaybookFilterChange = (value) => {
    dispatch(setFilter({ playbooks: value }))
    dispatch(
      fetchCategoriesByOrgSectionAndPlaybooks(
        isBaltoAdmin ? filters.organization : effectiveOrganization,
        DYNAMIC_PROMPT,
        value
      )
    )
    dispatch(
      fetchCategoriesByOrgSectionAndPlaybooks(
        isBaltoAdmin ? filters.organization : effectiveOrganization,
        POSTCALL,
        value
      )
    )
  }

  const handleFilterSubmit = (section) => {
    // CSV uses its own export button instead of the filters button
    if (section !== CSV) {
      dispatch(applyFilters(section))
    }
  }

  const handleOrgSelect = (option, action) => {
    dispatch(clearOrgFilter())

    if (action.action === 'clear') {
      handleFilterChange({ organization: '' })
    } else {
      const orgSelected = option.value

      handleFilterChange({ organization: orgSelected })
      dispatch(fetchAllData(orgSelected))
    }
  }
  useEffect(() => {
    dispatch(fetchVoipCampaignsByOrg(filters.organization))
  }, [filters.startDate, filters.endDate, filters.organization])

  useEffect(() => {
    if (canSeeOrgHierarchyFilter && !isEmpty(filters.organizations)) {
      if (filters.organizations.length > 1) {
        dispatch(fetchPlaybooksByOrgs(filters.organizations, userOrgHierarchy))
      } else if (filters.organizations.length === 1 && filters.organizations[0]) {
        dispatch(fetchPlaybooksByOrg(filters.organizations[0]))
      }
      dispatch(setFilter({ openPlaybookCategories: [], playbooks: [] }))
    }
  }, [filters.organizations])

  // fetchAllData skips playbooks for hierarchy managers because the org selector's selection drives
  // the fetch above. A single-node hierarchy means that selector never mounts, so nothing else loads
  // them — the flattened hierarchy includes the user's own org, so length 1 means "loaded, no
  // children" while length 0 means "not loaded yet"
  useEffect(() => {
    if (
      !isBaltoAdmin &&
      isHierarchyManager &&
      flattenedUserOrgHierarchy.length === 1 &&
      data.fetchedPlaybooksOrg !== organizationId
    ) {
      dispatch(fetchPlaybooksByOrg(organizationId))
    }
  }, [flattenedUserOrgHierarchy, isBaltoAdmin, isHierarchyManager, organizationId])

  // None selected is treated as multiple — shows org-level widgets, disables single-org features
  const multipleOrgsSelected = canSeeOrgHierarchyFilter && filters.organizations?.length !== 1

  const getPlaybookOptionsLength = () => {
    const { playbooks } = analyticsStore.data

    if (!playbooks?.length) {
      return 0
    }

    if (playbooks[0]?.options) {
      return playbooks.reduce((sum, group) => sum + group.options.length, 0)
    }

    return playbooks.length
  }

  const isLoading = (sectionName) => {
    if (sectionName === OVERVIEW) {
      return loading.overviewPage
    }
    return loading[`${sectionName}Page`]
  }

  const getDisabledReason = () => {
    // Disable button while loading
    if (isLoading(currentSection)) {
      return 'Filters are loading...'
    }

    // Disable button if organization is not selected for admin
    if (isBaltoAdmin && !filters.organization) {
      return 'Select an organization'
    }

    // Disable button if date is over 62 days
    if (dateInvalid) {
      return 'Date is invalid'
    }

    // Max call duration is less than min call duration
    if (callDurationInvalid) {
      return 'Maximum call duration must be greater than minimum call duration'
    }

    return false
  }

  const handleScroll = () => {
    setScrolled(window.scrollY > 1)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (filters.playbooks.length > 0) {
      dispatch(fetchPlaybookData(filters))
    } else {
      dispatch(
        setFilter({
          deck: { includes: true, selected: [] },
          openCategories: [],
          openSubcategories: [],
        })
      )
    }
  }, [filters.playbooks, filters.dateRange])

  useEffect(() => {
    const selectedPlaybooks = data.playbookData.filter((playbook) => {
      const loadedCids = filters.playbooks.map((loadedPlaybook) => {
        return loadedPlaybook.value
      })
      return loadedCids.includes(playbook.cid)
    })
    const selectedFilters = {
      deck: [],
    }
    selectedPlaybooks.forEach(({ cid }) => {
      selectedFilters.deck.push(...filters.deck.selected.filter((item) => item.includes(cid)))
    })
    Object.entries(selectedFilters).forEach(([key, selected]) => {
      dispatch(setFilter({ [key]: { selected, includes: true } }))
    })
  }, [data.playbookData])

  useEffect(() => {
    const initialOpenSubcategories = []
    const initialOpenDeckCategories = []

    data.playbookData?.forEach((playbook) => {
      if (playbook.deck) {
        playbook.deck
          .filter((category) => category.items.some((item) => item.has_sub_items))
          .forEach((category) => {
            const categoryContext = `deck - ${category.category}`
            category.items.forEach((item) => {
              if (item.has_sub_items) {
                initialOpenSubcategories.push(`${categoryContext} - ${item.item}`)
              }
            })
            initialOpenDeckCategories.push(categoryContext)
          })
      }
    })
    const initialOpenCategories = [...initialOpenDeckCategories]
    dispatch(
      setFilter({
        openCategories: initialOpenCategories,
        openSubcategories: initialOpenSubcategories,
      })
    )
  }, [data.playbookData])

  const disabledReason = getDisabledReason()

  const deckOptions = formatNestedOptions(
    'deck',
    data.playbookData?.flatMap((playbook) => playbook.deck || []),
    {
      ...filters.deck,
      openCategories: filters.openCategories,
      openSubcategories: filters.openSubcategories,
    }
  )

  const deckSublists = Object.values(
    deckOptions.reduce((acc, obj) => {
      if (!acc[obj.playbookName]) {
        acc[obj.playbookName] = []
      }
      acc[obj.playbookName].push(obj)
      return acc
    }, {})
  )

  const clearAllEntries = (section) => {
    dispatch(setFilter({ [section]: { includes: filters[section].includes, selected: [] } }))
  }

  const handleToggleEntry = (option) => {
    const { value: entryContext } = option
    const splitEntry = entryContext.split(' - ')
    const [cid, section] = splitEntry

    const { selected: selectedFilters, includes } = filters[section]

    const isEntrySelected = selectedFilters.some((selected) => selected === entryContext)

    if (isEntrySelected) {
      // search lets us deselect all children when the parent is deselected
      const filtersToSelect = selectedFilters.filter((selected) => selected !== entryContext)
      dispatch(setFilter({ [section]: { includes, selected: filtersToSelect } }))
    } else {
      // if no response used, add the parent too
      const contextsToSelect = [entryContext]
      // if it's more than 4 it's a decklist entry
      if (section === 'deck' && splitEntry.length > 4) {
        const category = splitEntry[2]
        const entry = splitEntry[3]
        const parentContext = `${cid} - ${section} - ${category} - ${entry}`
        const isParentSelected = selectedFilters.some((selected) => selected === parentContext)

        if (!isParentSelected) {
          contextsToSelect.push(parentContext)
        }
      }
      dispatch(
        setFilter({ [section]: { includes, selected: [...selectedFilters, ...contextsToSelect] } })
      )
    }
  }

  const openCategory = (category) => {
    const { value, items } = category
    const subcategoriesToOpen = items
      .filter((item) => item.has_sub_items)
      .map((item) => `${value} - ${item.item}`)

    dispatch(
      setFilter({
        openCategories: [...filters.openCategories, value],
        openSubcategories: [...filters.openSubcategories, ...subcategoriesToOpen],
      })
    )
  }

  const closeCategory = (category) => {
    const { value } = category
    const updatedCategories = filters.openCategories.filter((categoryContext) => {
      const items = category.items.filter((cat) => {
        return cat.value === categoryContext
      })
      return categoryContext !== value && isEmpty(items)
    })
    const updatedSubcategories = filters.openSubcategories.filter((subCatContext) => {
      const items = category.items.filter((cat) => {
        return subCatContext.includes(cat.value)
      })
      return !subCatContext.includes(value) && isEmpty(items)
    })
    dispatch(
      setFilter({ openCategories: updatedCategories, openSubcategories: updatedSubcategories })
    )
  }

  const handleToggleCategory = (selectedCategory) => {
    if (!selectedCategory.expanded) {
      return closeCategory(selectedCategory)
    }
    return openCategory(selectedCategory)
  }

  return (
    <form
      data-testid="analytics-filters"
      className={classNames('filter-form', 'analytics-filter-form', { scrolled })}
      onSubmit={(event) => {
        event.preventDefault()
        handleFilterSubmit(currentSection)
      }}
    >
      <div>
        <div className="filter-grid">
          {isBaltoAdmin && (
            <OrganizationFilter
              options={organizationOptions}
              value={filters.organization}
              onChange={handleOrgSelect}
            />
          )}
          {canSeeOrgHierarchyFilter && (
            <FormField data-testid="organizations-hierarchy-filter">
              <label>Organizations</label>
              <OrganizationsFilter options={userOrgHierarchy} dispatchOnClose />
            </FormField>
          )}
          {currentSection !== CSV && (
            <FormField>
              <label>Agents</label>
              <MultiSelect
                isSelectAll
                label="Agent"
                options={!agentSearchQuery && data.agents.length > 1000 ? [] : data.agents}
                optionsLength={data.agents.length}
                loading={loading.agents}
                value={filters.agents}
                onInputChange={(inputValue) => {
                  setAgentSearchQuery(inputValue)
                }}
                onChange={(value) => {
                  handleFilterChange({ agents: value })
                }}
                noOptionsMessage={() =>
                  agentSearchQuery ? 'No results found' : 'Begin typing to search...'
                }
                fixedWidth
                disabled={
                  analyticsStore.loading.agents ||
                  isEmpty(analyticsStore.data.agents) ||
                  filters.organizations?.length > 1
                }
                {...getPlaceholderContent(analyticsStore.data, analyticsStore.loading, 'agents')}
              />
            </FormField>
          )}

          {currentSection === USAGE && (
            <FormField data-testid="user-status-select">
              <label>User Status</label>
              <Select
                label="User Status"
                isClearable={false}
                options={[
                  { label: 'All Users', value: true },
                  { label: 'Active Users Only', value: false },
                ]}
                value={filters.includeDeletedUsers}
                onChange={(value) => {
                  handleFilterChange({ includeDeletedUsers: value.value })
                }}
              />
            </FormField>
          )}

          <MultiFilter
            label="Tags"
            accessor="tags"
            store={analyticsStore}
            handler={handleFilterChange}
            disabled={
              analyticsStore.loading.tags ||
              isEmpty(analyticsStore.data.tags) ||
              filters.organizations?.length > 1
            }
          />

          <FormField data-testid="playbooks-multi-select">
            <label>Playbooks</label>
            <MultiSelect
              isSelectAll
              label="Playbook"
              options={analyticsStore.data.playbooks}
              optionsLength={getPlaybookOptionsLength()}
              loading={analyticsStore.loading.playbooks}
              value={analyticsStore.filters.playbooks}
              onChange={(value) => handlePlaybookFilterChange(value)}
              fixedWidth
              disabled={
                analyticsStore.loading.playbooks ||
                isEmpty(analyticsStore.data.playbooks) ||
                (!multipleOrgsSelected && filters.organizations?.length > 1)
              }
              {...getPlaceholderContent(analyticsStore.data, analyticsStore.loading, 'playbooks')}
            />
          </FormField>

          {currentSection === POSTCALL && (
            <MultiFilter
              label="Categories"
              accessor={`${currentSection}Categories`}
              store={analyticsStore}
              handler={handleFilterChange}
            />
          )}

          {currentSection === DYNAMIC_PROMPT && (
            <FormField>
              <label>Categories</label>
              <NestedSelect
                disabled={filters.playbooks.length === 0}
                options={deckOptions}
                anotherOptions={deckSublists}
                value={filters.deck.selected}
                includes={filters.deck.includes}
                openCategories={filters.openCategories}
                openSubcategories={filters.openSubcategories}
                section="deck"
                clearAll={clearAllEntries}
                toggleEntry={handleToggleEntry}
                toggleCategory={handleToggleCategory}
              />
            </FormField>
          )}

          <FormField>
            <label>Min. Call Duration</label>
            <Select
              isClearable={false}
              allowFreeText
              isNumberField
              placeholder="Minutes per call"
              options={minCallDurationOptions}
              value={filters.callDuration}
              onChange={(option, action) => {
                if (action.action === 'clear') {
                  handleFilterChange({ callDuration: '1' })
                } else {
                  handleFilterChange({ callDuration: option.value })
                }
              }}
            />
          </FormField>

          {currentSection !== CSV && (
            <FormField>
              <label>Max. Call Duration</label>
              <TextInput
                type="number"
                placeholder="Minutes per call"
                value={filters.maxCallDuration}
                onChange={(event) => {
                  handleFilterChange({ maxCallDuration: event.target.value || '' })
                }}
                error={callDurationInvalid}
              />
              {callDurationInvalid && <ErrorMessage content="Max must be greater than min" />}
            </FormField>
          )}

          <FormField>
            <label>Date Range</label>
            <DateRangePickerReusable
              data-testid="date-range-picker"
              startDate={filters.startDate}
              endDate={filters.endDate}
              // A saved filter names its preset here; a click writes '', which hands the name to the picker's memory.
              pickedPreset={filters.dateRange || undefined}
              onChange={({ selection }) => {
                handleDateRangeFilterChange(selection)
              }}
              error={dateInvalid}
            />
            {dateInvalid && <ErrorMessage content="Date range must be two months or less" />}
          </FormField>
          {sectionNotCsvOrUsage && (
            <WinsFilter
              filters={filters}
              onChange={(option, action) => {
                dispatch(setFilter({ isWin: action.action === 'clear' ? null : option.value }))
              }}
            />
          )}
          {!isEmpty(data.dispositions) && sectionNotCsvOrUsage && (
            <FormField>
              <MultiFilter
                label="Dispositions"
                accessor="dispositions"
                store={analyticsStore}
                handler={handleFilterChange}
              />
            </FormField>
          )}
          {sectionNotCsvOrUsage && (
            <FormField data-testid="voip-campaign-filter">
              <label>VOIP Campaigns</label>
              <MultiSelect
                isSelectAll
                {...getPlaceholderContent(
                  analyticsStore.data,
                  analyticsStore.loading,
                  'voipCampaigns'
                )}
                label="VOIP Campaign Name"
                options={data.voipCampaigns}
                disabled={isEmpty(data.voipCampaigns) || loading.voipCampaigns}
                loading={loading.voipCampaigns}
                value={filters.voipCampaigns}
                onChange={(value) => handleFilterChange({ voipCampaigns: value })}
              />
            </FormField>
          )}
        </div>
        <FormField>
          <Checkbox
            className="mt"
            data-testid="include-managers-checkbox"
            label="Include Managers"
            checked={filters.includeManagers}
            onChange={(e, { checked }) => {
              handleFilterChange({ includeManagers: checked })
            }}
          />
        </FormField>
      </div>

      <div className="filter-buttons">
        {currentSection !== CSV && (
          <FormField>
            <label className="visibility-hidden">&nbsp;</label>
            <div>
              <Button
                brand
                data-testid="filter-button"
                type="submit"
                loading={isLoading(currentSection)}
                disabled={Boolean(disabledReason)}
                disabledTooltip={disabledReason}
              >
                Filter
              </Button>
            </div>
          </FormField>
        )}
      </div>
    </form>
  )
}
